import React, { useRef } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { CASES, WHATSAPP_LINK } from '../data/mock';

function CaseTile({ c, idx, total, span }) {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 12;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -12;
    el.style.transform = `perspective(1200px) rotateY(${x}deg) rotateX(${y}deg)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = 'perspective(1200px) rotateY(0) rotateX(0)';
  };

  return (
    <article className={`reveal group relative ${span}`}>
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className="tilt relative overflow-hidden rounded-[10px] glass aspect-[16/10]"
      >
        <img
          src={c.laptop}
          alt={c.company}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[1500ms]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E]/95 via-[#0A0B0E]/35 to-transparent" />

        {/* Mobile inset on bigger cards */}
        {(idx === 0 || idx === 3) && (
          <img
            src={c.mobile} alt="mobile" loading="lazy"
            className="absolute right-6 top-6 w-[120px] md:w-[160px] aspect-[9/19] object-cover rounded-[20px] border border-white/15 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] hidden md:block"
          />
        )}

        <div className="absolute top-5 left-5 z-10">
          <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#0A0B0E]/70 backdrop-blur text-[#C8FF61] text-[10px] font-medium tracking-[0.18em] uppercase border border-[#C8FF61]/30">
            {c.tag}
          </span>
        </div>

        <div className="absolute top-5 right-5 z-10 font-mono-label text-[#F5F1E8]/60">
          0{idx + 1} / 0{total}
        </div>

        <div className="absolute left-6 right-6 bottom-6 z-10 text-[#F5F1E8]">
          <div className="flex items-end justify-between gap-4">
            <div className="min-w-0">
              <span className="font-mono-label text-[#8E8B85]">{c.sector}</span>
              <h3 className="mt-2 font-display-tight text-[26px] md:text-[36px] leading-[0.98] truncate">
                {c.company}
              </h3>
              <p className="mt-1 text-[12px] text-[#F5F1E8]/65 truncate">{c.plan} · {c.period}</p>
            </div>
            <div className="text-right shrink-0">
              <span className="font-serif-italic text-[20px] md:text-[28px] kinetic-grad">
                {c.metric}
              </span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 right-6 z-20 w-12 h-12 rounded-full bg-[#C8FF61] flex items-center justify-center text-[#0A0B0E] opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500">
          <ArrowUpRight size={18} />
        </div>
      </div>
    </article>
  );
}

export default function Cases() {
  // Asymmetric grid for 4 cases:
  // Row 1: 8 / 4
  // Row 2: 4 / 8
  const spans = ['md:col-span-8', 'md:col-span-4', 'md:col-span-4', 'md:col-span-8'];

  return (
    <section id="cases" className="py-24 md:py-36 relative">
      <div className="thesis-container">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end border-b border-white/8 pb-8 md:pb-12">
          <div className="md:col-span-2">
            <span className="font-mono-label text-[#8E8B85]">(04) — Cases</span>
          </div>
          <div className="md:col-span-7 reveal">
            <h2 className="font-display-tight text-[44px] md:text-[80px] leading-[0.92] text-[#F5F1E8]">
              Resultados <span className="font-serif-italic kinetic-grad">que falam</span> por si.
            </h2>
          </div>
          <div className="md:col-span-3 md:text-right reveal">
            <a href={WHATSAPP_LINK('Quero ver mais cases da Thesis.')} target="_blank" rel="noopener noreferrer"
              className="line-link cursor-hover inline-flex items-center gap-2 text-[14px] font-medium text-[#F5F1E8]">
              Pedir portfólio completo <ArrowUpRight size={14} />
            </a>
          </div>
        </div>

        <div className="mt-12 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-6">
          {CASES.map((c, i) => (
            <CaseTile key={c.id} c={c} idx={i} total={CASES.length} span={spans[i] || 'md:col-span-6'} />
          ))}
        </div>

        <p className="mt-10 font-mono-label text-[#8E8B85] reveal">
          * Cases representativos. Mockups das interfaces ilustram o tipo de entrega — marcas reais sob NDA.
        </p>
      </div>
    </section>
  );
}
